import { otherColors } from '@ehrTheme/colors';
import { Add } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { enqueueSnackbar } from 'notistack';
import { default as React, ReactElement, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AllStates,
  AVAILABLE_EMPLOYEE_ROLES,
  EmployeeDetails,
  formatDateForDisplay,
  getApiError,
  GetEmployeesResponse,
  RoleType,
  State,
  useSuccessQuery,
} from 'utils';
import { deleteUser, getEmployees, updateUser } from '../api/api';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { CustomDialog } from '../components/dialogs/CustomDialog';
import Loading from '../components/Loading';
import { RoundedButton } from '../components/RoundedButton';
import { EMPLOYEE_ROWS_PER_PAGE, PROVIDER_ROWS_PER_PAGE } from '../constants';
import { dataTestIds } from '../constants/data-test-ids';
import { useApiClients } from '../hooks/useAppClients';
import useEvolveUser, { EvolveUser } from '../hooks/useEvolveUser';

export enum EmployeeTypes {
  employees = 'employees',
  providers = 'providers',
}

export default function EmployeesPage({ employeeType }: { employeeType: EmployeeTypes }): ReactElement {
  const { oystehrZambda } = useApiClients();
  const currentUser = useEvolveUser();
  const [employees, setEmployees] = useState<EmployeeDetails[]>([]);

  const [pageStates, setPageStates] = useState<{
    [key in EmployeeTypes]: {
      pageNumber: number;
      rowsPerPage: number;
      searchText: string;
      lastLoginFilterChecked: boolean;
      selectedState?: State | null;
    };
  }>({
    [EmployeeTypes.employees]: {
      pageNumber: 0,
      rowsPerPage: EMPLOYEE_ROWS_PER_PAGE,
      searchText: '',
      lastLoginFilterChecked: false,
    },
    [EmployeeTypes.providers]: {
      pageNumber: 0,
      rowsPerPage: PROVIDER_ROWS_PER_PAGE,
      searchText: '',
      lastLoginFilterChecked: false,
      selectedState: null,
    },
  });

  const emptyEmployeeList: EmployeeDetails[] = [];

  const handlePageStateChange = useCallback(
    (tab: EmployeeTypes, newPageState: Partial<(typeof pageStates)[EmployeeTypes.providers]>) => {
      setPageStates((prev) => ({
        ...prev,
        [tab]: {
          ...prev[tab],
          ...newPageState,
        },
      }));
    },
    []
  );

  const queryResult = useQuery({
    queryKey: ['get-employees'],
    queryFn: () => (oystehrZambda ? getEmployees(oystehrZambda) : Promise.resolve(null)),

    enabled: !!oystehrZambda,
  });

  useSuccessQuery(queryResult.data, (data: GetEmployeesResponse | null) => {
    setEmployees(data?.employees ?? emptyEmployeeList);
  });

  const { isFetching } = queryResult;

  return (
    <Box sx={{ width: '100%', marginTop: 2 }}>
      {isFetching && <Loading />}
      <EmployeesTable
        employees={employees}
        currentUser={currentUser}
        currentTab={employeeType}
        pageNumber={pageStates[employeeType].pageNumber}
        rowsPerPage={pageStates[employeeType].rowsPerPage}
        searchText={pageStates[employeeType].searchText}
        lastLoginFilterChecked={pageStates[employeeType].lastLoginFilterChecked}
        selectedState={pageStates[employeeType].selectedState}
        onPageStateChange={(newPageState) => handlePageStateChange(employeeType, newPageState)}
      />
    </Box>
  );
}

interface EmployeesTableProps {
  employees: EmployeeDetails[];
  currentUser: EvolveUser | undefined;
  currentTab: EmployeeTypes;
  pageNumber: number;
  rowsPerPage: number;
  searchText: string;
  lastLoginFilterChecked: boolean;
  selectedState?: State | null;
  onPageStateChange: (newPageState: {
    pageNumber?: number;
    rowsPerPage?: number;
    searchText?: string;
    lastLoginFilterChecked?: boolean;
    selectedState?: State | null;
  }) => void;
}

function EmployeesTable({
  employees,
  currentUser,
  currentTab,
  pageNumber,
  rowsPerPage,
  searchText,
  lastLoginFilterChecked,
  selectedState,
  onPageStateChange,
}: EmployeesTableProps): ReactElement {
  const theme = useTheme();
  const canEditRoles = currentUser?.hasRole([RoleType.Administrator, RoleType.CustomerSupport]) ?? false;

  // Filter the employees based on the search text
  const filteredEmployees: EmployeeDetails[] = useMemo(() => {
    const filtered = employees.filter((employee: EmployeeDetails) => {
      const name = (function () {
        if (employee.firstName && employee.lastName) return [employee.lastName, employee.firstName].join(', ');
        else if (employee.name) return employee.name;
        else return '';
      })();

      const lastLoginFilter = (function () {
        if (!lastLoginFilterChecked) return true;
        if (employee.lastLogin) return DateTime.fromISO(employee.lastLogin) > DateTime.now().minus({ days: 90 });
        else return false;
      })();

      return (
        name.toLowerCase().includes(searchText.toLowerCase()) &&
        (currentTab === EmployeeTypes.providers ? employee.isProvider : true) &&
        (currentTab === EmployeeTypes.providers && selectedState && selectedState.value !== ''
          ? employee.licenses.some((license) => license.state === selectedState.value)
          : true) &&
        lastLoginFilter
      );
    });

    // On the Employees tab, surface pending-review users at the top while preserving existing order otherwise.
    if (currentTab === EmployeeTypes.employees) {
      return filtered
        .map((employee, index) => ({ employee, index }))
        .sort((a, b) => {
          const aReview = a.employee.needsReview ? 0 : 1;
          const bReview = b.employee.needsReview ? 0 : 1;
          if (aReview !== bReview) return aReview - bReview;
          return a.index - b.index;
        })
        .map(({ employee }) => employee);
    }

    return filtered;
  }, [employees, searchText, currentTab, selectedState, lastLoginFilterChecked]);

  // For pagination, only include the rows that are on the current page
  const pageEmployees: EmployeeDetails[] = React.useMemo(
    () =>
      filteredEmployees.slice(
        pageNumber * rowsPerPage, // skip over the rows from previous pages
        (pageNumber + 1) * rowsPerPage // only show the rows from the current page
      ),
    [filteredEmployees, pageNumber, rowsPerPage]
  );

  // Handle pagination
  const handleChangePage = useCallback(
    (event: unknown, newPageNumber: number): void => {
      onPageStateChange({ pageNumber: newPageNumber });
    },
    [onPageStateChange]
  );

  // Handle changing the number of rows per page
  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      onPageStateChange({ rowsPerPage: parseInt(event.target.value), pageNumber: 0 });
    },
    [onPageStateChange]
  );

  // Handle changing the search text
  const handleChangeSearchText = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
      onPageStateChange({ searchText: event.target.value, pageNumber: 0 });
    },
    [onPageStateChange]
  );

  const handleChangeStateSelect = useCallback(
    (_event: any, value: State | null): void => {
      onPageStateChange({ selectedState: value });
    },
    [onPageStateChange]
  );

  const handleChangeLastLoginFilter = useCallback(
    (_event: any, value: boolean): void => {
      onPageStateChange({ lastLoginFilterChecked: value, pageNumber: 0 });
    },
    [onPageStateChange]
  );

  const showReviewColumn = currentTab === EmployeeTypes.employees;

  return (
    <>
      <Paper sx={{ padding: 2 }}>
        <TableContainer>
          <Grid container direction="row" justifyContent="start" alignItems="center">
            {/* Employee Name Search Box */}
            <Box sx={{ display: 'flex', flex: 2, margin: '10px 0' }}>
              <TextField
                id="outlined-basic"
                label="Search by name"
                variant="outlined"
                onChange={handleChangeSearchText}
                value={searchText}
                data-testid={dataTestIds.employeesPage.searchByName}
                InputProps={{ endAdornment: <SearchIcon /> }}
                sx={{ width: '100%', paddingRight: 2 }}
              />
            </Box>
            {/* States drop-down */}
            {currentTab === EmployeeTypes.providers && (
              <Box sx={{ display: 'flex', flex: 2, paddingRight: 3 }}>
                <StateSelect onChange={handleChangeStateSelect} selectedState={selectedState} />
              </Box>
            )}
            <Box sx={{ display: 'flex', flex: 1, maxWidth: '260px' }}>
              <FormControlLabel
                name="last_login_filter"
                control={<Checkbox checked={lastLoginFilterChecked} onChange={handleChangeLastLoginFilter} />}
                label="Hide last logins that occurred more than 90 days ago"
                sx={{ '.MuiFormControlLabel-asterisk': { display: 'none' } }}
              />
            </Box>
            {/* todo reduce code duplicate */}
            {canEditRoles ? (
              <Link to={`/admin/employees/add`}>
                <Button variant="contained" sx={{ marginLeft: 1 }} startIcon={<Add />}>
                  Add user
                </Button>
              </Link>
            ) : (
              <Tooltip title="You must be an administrator to add new users" placement="top">
                <span>
                  {/* https://mui.com/material-ui/react-tooltip/#disabled-elements */}
                  <Button variant="contained" sx={{ marginLeft: 1 }} startIcon={<Add />} disabled>
                    Add user
                  </Button>
                </span>
              </Tooltip>
            )}
          </Grid>

          {/* Employees Table */}
          <Table sx={{ minWidth: 650 }} aria-label="locationsTable" data-testid={dataTestIds.employeesPage.table}>
            <TableHead>
              <TableRow sx={{ '& .MuiTableCell-head': { fontWeight: 'bold', textAlign: 'left' } }}>
                <TableCell sx={{ width: '25%' }}>Name (Last, First)</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Status</TableCell>
                {currentTab === EmployeeTypes.providers && (
                  <>
                    <TableCell sx={{ maxWidth: '150px' }}>Getting alerts</TableCell>
                    <TableCell sx={{ maxWidth: '150px' }}>Seen patient last 30 mins</TableCell>
                  </>
                )}
                {showReviewColumn && <TableCell sx={{ width: '220px', whiteSpace: 'nowrap' }}>Actions</TableCell>}
              </TableRow>
            </TableHead>

            <TableBody>
              {pageEmployees.map((employee) => {
                const name = (function () {
                  if (employee.firstName && employee.lastName)
                    return [employee.lastName, employee.firstName].join(', ');
                  else if (employee.name) return employee.name;
                  else return '-';
                })();

                return (
                  <TableRow key={employee.id} sx={{ '& .MuiTableCell-body': { textAlign: 'left' } }}>
                    <TableCell>
                      {employee.needsReview ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>{name}</span>
                          <Chip
                            label="NEEDS REVIEW"
                            data-testid={dataTestIds.employeesPage.needsReviewChip}
                            sx={{
                              backgroundColor: otherColors.orange100,
                              color: otherColors.orange800,
                              borderRadius: '4px',
                              height: '18px',
                              '& .MuiChip-label': {
                                padding: '0 8px',
                                lineHeight: '18px',
                              },
                              ...theme.typography.subtitle2,
                            }}
                          />
                        </Stack>
                      ) : (
                        <Link
                          to={`/admin/employee/${employee.id}`}
                          style={{
                            display: 'contents',
                            color: theme.palette.primary.main,
                          }}
                        >
                          {name}
                        </Link>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: otherColors.tableRow,
                      }}
                    >
                      {employee.phoneNumber || '-'}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: otherColors.tableRow,
                        maxWidth: '220px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {employee.email ? (
                        <Tooltip title={employee.email} placement="top">
                          <span>{employee.email}</span>
                        </Tooltip>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: otherColors.tableRow,
                      }}
                    >
                      {employee.lastLogin ? formatDateForDisplay(employee.lastLogin) : 'Never'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={employee.status.toUpperCase()}
                        data-testid={dataTestIds.employeesPage.statusChip}
                        sx={{
                          backgroundColor:
                            employee.status === 'Active'
                              ? otherColors.employeeActiveChip
                              : otherColors.employeeDeactivatedChip,
                          color:
                            employee.status === 'Active'
                              ? otherColors.employeeActiveText
                              : otherColors.employeeDeactivatedText,
                          borderRadius: '4px',
                          height: '18px',
                          '& .MuiChip-label': {
                            padding: '0 8px',
                            lineHeight: '18px',
                          },
                          ...theme.typography.subtitle2,
                        }}
                      />
                    </TableCell>
                    {currentTab === EmployeeTypes.providers && (
                      <>
                        <TableCell
                          sx={{
                            color: otherColors.tableRow,
                          }}
                        >
                          {employee.gettingAlerts && (
                            <Chip
                              label="GETTING ALERTS"
                              color={'info'}
                              sx={{
                                borderRadius: '4px',
                                bgcolor: 'info.light',
                                color: 'info.dark',
                                height: '18px',
                                '& .MuiChip-label': {
                                  padding: '0 8px',
                                  lineHeight: '18px',
                                },
                                ...theme.typography.subtitle2,
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: otherColors.tableRow,
                          }}
                        >
                          {employee.seenPatientRecently && (
                            <Chip
                              label="BEEN SEEN"
                              sx={{
                                backgroundColor: otherColors.employeeBeenSeenChip,
                                color: otherColors.employeeBeenSeenText,
                                borderRadius: '4px',
                                height: '18px',
                                '& .MuiChip-label': {
                                  padding: '0 8px',
                                  lineHeight: '18px',
                                },
                                ...theme.typography.subtitle2,
                              }}
                            />
                          )}
                        </TableCell>
                      </>
                    )}
                    {showReviewColumn && (
                      <TableCell>
                        {employee.needsReview && canEditRoles && <PendingReviewActions employee={employee} />}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredEmployees.length}
            rowsPerPage={rowsPerPage}
            page={pageNumber}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            data-testid={dataTestIds.pagination.paginationContainer}
          />
        </TableContainer>
      </Paper>
    </>
  );
}

interface PendingReviewActionsProps {
  employee: EmployeeDetails;
}

function PendingReviewActions({ employee }: PendingReviewActionsProps): ReactElement {
  const { oystehrZambda } = useApiClients();
  const queryClient = useQueryClient();
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<RoleType[]>([]);

  const assignMutation = useMutation({
    mutationFn: async (roles: RoleType[]) => {
      if (!oystehrZambda) throw new Error('Zambda Client not found');
      return updateUser(oystehrZambda, {
        userId: employee.id,
        selectedRoles: roles,
      });
    },
    onSuccess: async () => {
      enqueueSnackbar('Role assigned successfully.', { variant: 'success' });
      setAssignOpen(false);
      setSelectedRoles([]);
      await queryClient.invalidateQueries({ queryKey: ['get-employees'] });
    },
    onError: () => {
      enqueueSnackbar('Failed to assign role. Please try again.', { variant: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!oystehrZambda) throw new Error('Zambda Client not found');
      return deleteUser(oystehrZambda, { userId: employee.id });
    },
    onSuccess: async () => {
      enqueueSnackbar('User deleted.', { variant: 'success' });
      await queryClient.invalidateQueries({ queryKey: ['get-employees'] });
    },
  });

  return (
    <>
      <Stack direction="row" spacing={1}>
        <RoundedButton
          size="small"
          data-testid={dataTestIds.employeesPage.assignRoleButton}
          onClick={() => setAssignOpen(true)}
        >
          Assign Role
        </RoundedButton>
        <ConfirmationDialog
          title="Delete user?"
          description={`This will permanently delete ${
            employee.email || employee.name
          } and block EHR access. This cannot be undone.`}
          response={async () => {
            try {
              await deleteMutation.mutateAsync();
            } catch (error) {
              enqueueSnackbar(getApiError({ error, defaultError: 'Failed to delete user.' }), { variant: 'error' });
            }
          }}
          actionButtons={{
            proceed: {
              text: 'Delete',
              color: 'error',
              loading: deleteMutation.isPending,
            },
          }}
        >
          {(showDialog) => (
            <RoundedButton
              size="small"
              color="error"
              data-testid={dataTestIds.employeesPage.quickDeleteButton}
              onClick={showDialog}
            >
              Delete
            </RoundedButton>
          )}
        </ConfirmationDialog>
      </Stack>
      <CustomDialog
        open={assignOpen}
        handleClose={() => {
          setAssignOpen(false);
          setSelectedRoles([]);
        }}
        dataTestId={dataTestIds.employeesPage.assignRoleDialog}
        title="Assign role"
        description={
          <Box sx={{ width: 360 }}>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel id="assign-role-label">Role</InputLabel>
              <Select
                labelId="assign-role-label"
                label="Role"
                multiple
                value={selectedRoles}
                inputProps={{ 'data-testid': dataTestIds.employeesPage.assignRoleSelect }}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedRoles(
                    typeof value === 'string' ? (value.split(',') as RoleType[]) : (value as RoleType[])
                  );
                }}
                renderValue={(selected) =>
                  AVAILABLE_EMPLOYEE_ROLES.filter((role) => (selected as RoleType[]).includes(role.value))
                    .map((role) => role.label)
                    .join(', ')
                }
              >
                {AVAILABLE_EMPLOYEE_ROLES.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    <Checkbox checked={selectedRoles.includes(role.value)} />
                    <ListItemText primary={role.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        }
        closeButtonText="Cancel"
        confirmText="Save"
        disabled={selectedRoles.length === 0}
        confirmLoading={assignMutation.isPending}
        handleConfirm={() => {
          if (selectedRoles.length > 0) {
            assignMutation.mutate(selectedRoles);
          }
        }}
      />
    </>
  );
}

/*
 * A general purpose US states select. Might be a good candidate for moving to a
 * separate file.
 */
interface StateSelectProps {
  onChange: (event: React.SyntheticEvent<Element, Event>, value: State | null) => void;
  selectedState?: State | null;
}

function StateSelect({ onChange, selectedState }: StateSelectProps): ReactElement {
  const EMPTY_STATE = { label: 'All states', value: '' };
  const options = [EMPTY_STATE, ...AllStates];

  return (
    <Autocomplete
      value={selectedState || EMPTY_STATE}
      onChange={onChange}
      data-testid={dataTestIds.employeesPage.providersStateFilter}
      getOptionLabel={(state) => state.label || 'Unknown'}
      isOptionEqualToValue={(option, tempValue) => option.value === tempValue.value}
      options={options}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option.value}>
            {option.label}
          </li>
        );
      }}
      fullWidth
      renderInput={(params) => <TextField name="state" {...params} label="State" />}
    />
  );
}
